
import { useEffect, useRef, useMemo } from 'react';
import { DollarSign, Loader2, Shield, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { sileo } from 'sileo';
import { Header } from '../../widgets/header';
import { ChartsSection } from '../../widgets/charts-section';
import { Card, CardHeader, CardTitle, CardContent } from '../../shared/ui';
import { useDashboardMetrics } from '../../shared/api';
import { ExportButton } from '../../features/export-report';
import { useAnalysisStore } from '../../shared/lib/store/analysisStore';

export function AnalysisPage() {
  const { t } = useTranslation();
  const { data: metrics, isLoading } = useDashboardMetrics();
  const toastShown = useRef(false);
  const localProjectData = useAnalysisStore(state => state.projectData);

  // ── Accumulate stats from local analysis store ─────────────────────────
  const localStats = useMemo(() => {
    let totalIssues = 0;
    let bugs = 0;
    let vulnerabilities = 0;
    let codeSmells = 0;
    let critical = 0;
    let totalAnalyses = 0;

    Object.values(localProjectData).forEach((project: any) => {
      totalAnalyses += Number(project.results?.length || 0);
      project.aggregatedIssues?.forEach((issue: any) => {
        totalIssues++;
        const sev = (issue.severity || '').toLowerCase();
        if (sev === 'critical') { vulnerabilities++; critical++; }
        else if (sev === 'high') bugs++;
        else if (sev === 'medium') codeSmells++;
      });
    });

    return { totalIssues, bugs, vulnerabilities, codeSmells, critical, totalAnalyses };
  }, [localProjectData]);

  // ── Compute total issues (API is object, not number) ───────────────────
  const apiTotalIssues =
    Number(metrics?.total_issues?.critical || 0) +
    Number(metrics?.total_issues?.high || 0) +
    Number(metrics?.total_issues?.medium || 0) +
    Number(metrics?.total_issues?.low || 0);

  const totalIssues = (localStats.totalIssues || 0) + apiTotalIssues;
  const criticalCount =
    Number(localStats.vulnerabilities || 0) + Number(metrics?.total_issues?.critical || 0);
  const highCount = 
    Number(localStats.bugs || 0) + Number(metrics?.total_issues?.high || 0);

  const countCritical = Number(localStats.critical || 0) + Number(metrics?.total_issues?.critical || 0);
  const countHigh = highCount;
  const countMedium = Number(localStats.codeSmells || 0) + Number(metrics?.total_issues?.medium || 0);
  const countLow = Number(metrics?.total_issues?.low || 0);

  // ── Technical debt: weighted by severity (hours) ───────────────────────
  const estimatedDebtHours =
    (countCritical * 4) + (countHigh * 2) + (countMedium * 1) + (countLow * 0.5);

  const apiDebtHours = Number(metrics?.tech_debt_hours);
  const techDebtHoursRaw = (!isNaN(apiDebtHours) && apiDebtHours > 0)
    ? apiDebtHours
    : (isNaN(estimatedDebtHours) ? 0 : estimatedDebtHours);
  
  const techDebtHoursStr = techDebtHoursRaw.toFixed(0);

  // ── Debt ratio = severe (critical+high) / total * 100 ──────────────────
  const safeTotalIssues = isNaN(totalIssues) ? 0 : totalIssues;
  const debtRatio =
    safeTotalIssues > 0
      ? (((countCritical + countHigh) / safeTotalIssues) * 100).toFixed(1)
      : '0.0';

  // ── Cost estimate: $75 per hour of debt ────────────────────────────────
  const costEstimate = ((techDebtHoursRaw * 75) / 1000).toFixed(1);

  // Combined totals (local store + API)
  const combinedTotalIssues = totalIssues || 0;
  const combinedTotalAnalyses =
    (localStats.totalAnalyses || 0) + Number(metrics?.total_analyses || 0);

  useEffect(() => {
    const hasData = combinedTotalIssues > 0 || combinedTotalAnalyses > 0;
    if (!toastShown.current && hasData) {
      toastShown.current = true;
      sileo.success({
        title: t('toasts.analysisComplete'),
        description: t('toasts.analysisCompleteDesc', {
          count: combinedTotalIssues,
          analyses: combinedTotalAnalyses,
        }),
        icon: <Shield className="size-3.5" />,
      });

      if (criticalCount > 0) {
        sileo.warning({
          title: t('toasts.criticalDetected'),
          description: t('toasts.criticalDetectedDesc', { count: criticalCount }),
          icon: <AlertTriangle className="size-3.5" />,
        });
      }
    }
  }, [combinedTotalIssues, combinedTotalAnalyses, criticalCount, t]);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-8 py-8 space-y-8">
        <div className="flex items-center justify-between">
          <h1 className="text-4xl font-bold text-text-primary font-mono lowercase tracking-tighter">devlint/analysis</h1>
          <ExportButton />
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-3 text-text-secondary font-mono">
              {t('common.loading')}
            </span>
          </div>
        )}

        {/* Technical Debt Section */}
        <section>
          <h2 className="text-2xl font-semibold text-text-primary font-mono mb-6 uppercase tracking-widest text-xs">
            {t('analysis.technicalDebt')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Total Debt */}
            <Card variant="highlighted" corners="asymmetric">
              <CardHeader>
                <CardTitle className="text-xs font-mono uppercase opacity-70">{t('analysis.totalDebt')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-primary font-mono">
                    {techDebtHoursStr}h
                  </span>
                </div>
                <p className="text-[10px] text-text-muted font-mono mt-2 uppercase">
                  {t('analysis.totalDebtDesc')}
                </p>
              </CardContent>
            </Card>

            {/* Debt Ratio */}
            <Card corners="asymmetric-inverse">
              <CardHeader>
                <CardTitle className="text-xs font-mono uppercase opacity-70">{t('analysis.debtRatio')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-warning font-mono">
                    {debtRatio}%
                  </span>
                </div>
                <p className="text-[10px] text-text-muted font-mono mt-2 uppercase">
                  {t('analysis.debtRatioDesc')}
                </p>
              </CardContent>
            </Card>

            {/* Cost Estimate */}
            <Card corners="asymmetric">
              <CardHeader>
                <CardTitle className="text-xs font-mono uppercase opacity-70">{t('analysis.costEstimate')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <DollarSign className="h-8 w-8 text-success" />
                  <span className="text-4xl font-bold text-success font-mono">
                    {costEstimate}K
                  </span>
                </div>
                <p className="text-[10px] text-text-muted font-mono mt-2 uppercase">
                  {t('analysis.costEstimateDesc')}
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Charts — same as dashboard */}
        <section>
          <h2 className="text-2xl font-semibold text-text-primary font-mono mb-6 uppercase tracking-widest text-xs">
            {t('analysis.detailedAnalysis')}
          </h2>
          <ChartsSection metrics={metrics} localStats={localStats} />
        </section>

        {/* Global Summary Card */}
        <Card corners="asymmetric-inverse">
          <CardHeader>
            <CardTitle className="text-xs font-mono uppercase opacity-70">{t('analysis.globalSummary')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-6 text-center lg:text-left">
              <div>
                <p className="text-[10px] text-text-secondary font-mono mb-1 uppercase tracking-tighter">
                  {t('metrics.totalProjects')}
                </p>
                <p className="text-2xl font-bold text-text-primary font-mono">
                  {Number(metrics?.total_projects || 0)}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-text-secondary font-mono mb-1 uppercase tracking-tighter">
                  {t('metrics.totalAnalyses')}
                </p>
                <p className="text-2xl font-bold text-text-primary font-mono">
                  {combinedTotalAnalyses || 0}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-text-secondary font-mono mb-1 uppercase tracking-tighter">
                  {t('metrics.bugs')}
                </p>
                <p className="text-2xl font-bold text-error font-mono">
                  {highCount || 0}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-text-secondary font-mono mb-1 uppercase tracking-tighter">
                  {t('metrics.codeSmells')}
                </p>
                <p className="text-2xl font-bold text-warning font-mono">
                  {(Number(localStats.codeSmells || 0) + Number(metrics?.total_issues?.medium || 0)) || 0}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-text-secondary font-mono mb-1 uppercase tracking-tighter">
                  {t('metrics.vulnerabilities')}
                </p>
                <p className="text-2xl font-bold text-error font-mono">
                  {criticalCount || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
